import { saveAs } from '@progress/kendo-file-saver'
import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  ShareDropDownItemButton,
  type ShareDropDownItemButtonProps,
} from '~/components/features/ShareDropDownItemButton'
import { useLogEvent } from '~/hooks/useLogEvent'
import { useNotify } from '~/hooks/useNotify'
import { createImageData, exists } from '~/utils'

export type DownloadAsPngButtonProps = Pick<
  ShareDropDownItemButtonProps,
  'disabled'
> & {
  'data-testid'?: string
  name?: string
  image: HTMLElement | React.ReactElement
}

export const DownloadAsPngButton = ({
  'data-testid': dataTestId = 'components-features-download-as-png-button',
  disabled,
  image,
  name = 'table',
}: DownloadAsPngButtonProps) => {
  const { t } = useTranslation()
  const logEvent = useLogEvent()
  const notify = useNotify()

  const handleClick = React.useCallback<
    EH<ShareDropDownItemButtonProps, 'onClick'>
  >(async () => {
    const [data, fileName] = await createImageData(image, name)

    if (!exists(data) || !exists(fileName)) {
      notify({ content: t('png_file_generation_failed'), style: 'error' })

      return
    }

    saveAs(data, fileName)

    logEvent('Download PNG', { withSuffix: true })
  }, [image, logEvent, name, notify, t])

  return (
    <ShareDropDownItemButton
      data-testid={dataTestId}
      disabled={disabled}
      label={t('download_png')}
      onClick={handleClick}
    />
  )
}
